import React, { useEffect, useRef, useState } from 'react';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';

/**
 * „Új verzió érhető el” sáv.
 *
 * A build (`npm run build`) a `public/version.json`-ba írja a build azonosítót (scripts/write-version.js).
 * Betöltéskor megjegyezzük a kiszolgált verziót, majd 3 percenként / fülre visszatéréskor / service worker
 * frissüléskor újra lekérjük: ha más, a felhasználó látja, hogy régi build fut nála, és egy kattintással frissít.
 * 30 perc után (ha nem frissített) a következő fül-váltásnál automatikusan újratöltünk.
 */
const CHECK_MS = 3 * 60 * 1000;
const AUTO_RELOAD_AFTER_MS = 30 * 60 * 1000;

async function fetchVersion(): Promise<string | null> {
  try {
    const r = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!r.ok) return null;
    const j = await r.json();
    return String(j.build || j.version || '');
  } catch { return null; }
}

const UpdateBanner: React.FC = () => {
  const [outdated, setOutdated] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const current = useRef<string | null>(null);
  const detectedAt = useRef<number | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return; // fejlesztésnél a HMR frissít
    let stopped = false;
    const check = async () => {
      const v = await fetchVersion();
      if (stopped || !v) return;
      if (current.current === null) { current.current = v; return; }
      if (v !== current.current) {
        if (!detectedAt.current) detectedAt.current = Date.now();
        setOutdated(true);
        // Ha már régóta nem frissített és épp visszatért a fülre: automatikus újratöltés
        if (document.visibilityState === 'visible' && Date.now() - detectedAt.current > AUTO_RELOAD_AFTER_MS) reload();
      }
    };
    check();
    const iv = setInterval(check, CHECK_MS);
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    // Új service worker aktiválódott → biztosan új build van
    const onCtrl = () => check();
    navigator.serviceWorker?.addEventListener?.('controllerchange', onCtrl);
    return () => { stopped = true; clearInterval(iv); document.removeEventListener('visibilitychange', onVis); navigator.serviceWorker?.removeEventListener?.('controllerchange', onCtrl); };
  }, []);

  const reload = async () => {
    try {
      const reg = await navigator.serviceWorker?.getRegistration?.();
      await reg?.update?.();
      reg?.waiting?.postMessage({ type: 'SKIP_WAITING' });
    } catch { /* ignore */ }
    window.location.reload();
  };

  if (!outdated || dismissed) return null;
  return (
    <div role="status" className="fixed bottom-3 left-1/2 -translate-x-1/2 z-[9999] max-w-[calc(100vw-24px)] w-[440px] rounded-xl border border-primary-200 dark:border-primary-500/40 bg-white dark:bg-gray-800 shadow-lg px-3 py-2.5 flex items-center gap-3">
      <span className="w-8 h-8 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-300 inline-flex items-center justify-center flex-shrink-0"><ArrowPathIcon className="w-4 h-4" /></span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Új verzió érhető el</div>
        <div className="text-xs text-gray-500 dark:text-gray-400">Frissíts, hogy a legújabb javításokat használd.</div>
      </div>
      <button type="button" onClick={reload} className="crm-btn crm-btn-primary h-8 text-xs flex-shrink-0">Frissítés</button>
      <button type="button" onClick={() => setDismissed(true)} className="w-7 h-7 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" aria-label="Később"><XMarkIcon className="w-4 h-4" /></button>
    </div>
  );
};

export default UpdateBanner;
